if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.cacheAllSlides();
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];

        // Use only currently rendered, non-duplicate slides, in DOM order
        const allMedia = Array.from(
          this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
        );

        allMedia.forEach((media, i) => {
          const mediaType = media.dataset.mediaType;

          // Prefer pswpIndex (group-local), fall back to loop index
          const id = Number(media.dataset.pswpIndex ?? i);

          if (mediaType === 'model') {
            dataSource.push({
              id,
              html: `<div class="pswp__item--${mediaType}">${media.querySelector('product-model').outerHTML}</div>`,
              mediaType
            });
            return;
          }

          if (mediaType === 'video' || mediaType === 'external_video') {
            dataSource.push({
              id,
              html: `<div class="pswp__item--${mediaType}">${media.querySelector('video-element').outerHTML}</div>`,
              mediaType
            });
            return;
          }

          // image
          const imgEl = media.querySelector('img');
          const src = media.dataset.src || imgEl?.src || imgEl?.getAttribute('data-src') || '';
          dataSource.push({
            id,
            src,
            width: Number(media.dataset.pswpWidth || imgEl?.naturalWidth || 0),
            height: Number(media.dataset.pswpHeight || imgEl?.naturalHeight || 0),
            mediaType
          });
        });

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false
        });

        // Keep your existing filters; they’re fine
        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) return el;
            }
          }
          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) return el.src;
            }
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia?.(this);
        });

        this.lightbox.on('destroy', () => {
          // When closing zoom, return Swiper to the same *group-local* item
          const { currIndex } = this.lightbox.pswp;
          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        // Button UI stays the same as your code...
        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => this.lightbox.pswp.next()
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => this.lightbox.pswp.prev()
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: () => this.lightbox.pswp.close()
          });
        });

        this.lightbox.init();

        // IMPORTANT: open with pswpIndex (group-local), not global mediaIndex
        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, target) => {
            // Find the slide wrapper first; use its pswpIndex
            const slideEl = target.closest('.product__media-item');
            const index =
              Number(slideEl?.dataset?.pswpIndex ?? target.dataset.pswpIndex ?? target.dataset.mediaIndex ?? 0);

            this.lightbox.loadAndOpen(index);
          }
        });

      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        // Find the slide for this featured media in the ORIGINAL DOM (cached or live)
        const allSlides = this._cache?.slides || Array.from(
          this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
        );
        const match = allSlides.find(
          el => el.dataset.mediaId === String(variant.featured_media.id)
        );

        // If we can read the group from the matched slide, filter the gallery to that group
        const groupId = match?.dataset?.variantGroup;

        if (groupId) {
          // Filter DOM to this group and reinit sliders
          this.applyVariantGroup(groupId, variant.featured_media.id);
          return;
        }

        // Fallback behavior (no grouping available): original logic
        if (this.sliderInstance?.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }

      // Cache the full, unfiltered DOM so we can rebuild per variant
      cacheAllSlides() {
        if (!this._cache) this._cache = {};
        if (!this._cache.slides) {
          // Use the original (non-duplicate) slides
          this._cache.slides = Array.from(
            this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
          );
        }
        if (!this._cache.thumbsWrapper) {
          this._cache.thumbsWrapper = this.elements?.thumbnails?.querySelector('.swiper-wrapper') || null;
        }
        if (!this._cache.thumbs && this._cache.thumbsWrapper) {
          this._cache.thumbs = Array.from(this._cache.thumbsWrapper.querySelectorAll('.product__thumbs-item'));
        }
      }

      // Build a fresh list of slides/thumbs for a given group (returns arrays)
      collectGroupDom(groupId) {
        const gid = String(groupId);
        const slides = (this._cache?.slides || []).filter(el => el.dataset.variantGroup === gid);
        const thumbs = (this._cache?.thumbs || []).filter(el => el.dataset.variantGroup === gid);
        return { slides, thumbs };
      }

      // Replace current wrappers with ONLY the group's nodes (cloned)
      renderGroup({ slides, thumbs }) {
        // Clear viewer slides wrapper
        this.elements.mediaList.innerHTML = '';
        slides.forEach(el => this.elements.mediaList.appendChild(el.cloneNode(true)));

        // Clear thumbs wrapper (if exists)
        if (this._cache?.thumbsWrapper && this.elements?.thumbnails) {
          const tw = this._cache.thumbsWrapper;
          tw.innerHTML = '';
          thumbs.forEach(el => tw.appendChild(el.cloneNode(true)));
        }
      }

      // Rebuild lightbox data after DOM rebuild
      rebuildLightboxIfNeeded() {
        if (!this.enableImageZoom) return;
        try {
          this.lightbox?.destroy();
        } catch (e) {}
        this.initImageZoom();
      }

      // Fully swap to a group: destroy sliders → render group → re-init → go to wanted slide
      applyVariantGroup(groupId, activeMediaId = null) {
        // Ensure we have the “full set” cached
        this.cacheAllSlides();

        // Destroy any running sliders so Swiper doesn’t keep stale slide refs
        this.destroySlider();
        if (typeof this.thumbsInstance === 'object') {
          try { this.thumbsInstance.slider?.destroy(true, true); } catch(e){}
          this.thumbsInstance = false;
        }

        // Render only the requested group
        const groupDom = this.collectGroupDom(groupId);
        this.renderGroup(groupDom);

        this.reindexPswp();

        // Re-init sliders on the reduced DOM
        this.initSlider();

        // If we know the specific media to land on, go there; otherwise index 0
        if (this.sliderInstance?.slider) {
          let targetIndex = 0;
          if (activeMediaId) {
            const slides = this.sliderInstance.slider.slides || [];
            for (let i = 0; i < slides.length; i++) {
              const s = slides[i];
              if (s.dataset && s.dataset.mediaId === String(activeMediaId)) {
                targetIndex = this.sliderInstance.slider.loopedSlides ? i : i;
                break;
              }
            }
          }
          // Use slideToLoop for safety if looped; slideTo otherwise
          if (typeof this.sliderInstance.slider.slideToLoop === 'function') {
            this.sliderInstance.slider.slideToLoop(targetIndex, 0, false);
          } else {
            this.sliderInstance.slider.slideTo(targetIndex, 0, false);
          }
        }

        // Rebuild lightbox datasource so zoom only sees visible slides
        this.rebuildLightboxIfNeeded();
      }

      // Assign a sequential index for the *currently rendered* (non-duplicate) slides,
      // and propagate it to common inner clickable elements.
      reindexPswp() {
        const slides = Array.from(
          this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
        );
        slides.forEach((el, i) => {
          const idx = String(i);
          el.dataset.pswpIndex = idx;

          // Propagate to inner elements that might receive the click
          el.querySelectorAll('.js-photoswipe--zoom, [data-media-index], img').forEach((child) => {
            child.dataset.pswpIndex = idx;
          });
        });
      }



    }
  );
}
